/**
 * Redaction at the source (D-259, D-032): every free text that may reach a platform log — an error message,
 * a stack, a client-reported text — passes through here before it is written or shipped. Platform logs carry
 * entity ids, route patterns, codes and durations; never amounts, names, phone numbers, documents, PINs,
 * passwords, TOTP codes, card codes or tokens. Structured fields are never logged from request bodies at all;
 * this module covers the free texts that cannot be avoided.
 *
 * Rules (applied in this order; all idempotent — redacting twice changes nothing):
 *  1. UUIDs are kept (entity ids are allowed, ADR-04 §2.6) and protected from the number rule.
 *  2. e-mail addresses → `[E]`.
 *  3. long secrets: hex runs of 24+ characters, base64url runs of 32+, upper-case letter + digit runs of 8+
 *     (card and transfer codes, D-087 / D-267) → `[T]`.
 *  4. numbers with 3 or more digits (Latin, Arabic-Indic or Persian digits; separators `, . ٬ ٫` and spaces
 *     inside) → `[N]` — amounts, phone and document numbers. Not when glued to an identifier on the left
 *     (`v8`, `chunk3141`) or after `:` (stack line / column positions).
 *  5. runs of Arabic letters → `[A]` — names typed by users are the likeliest personal data in an error text;
 *     the program's own messages to the logs are English.
 *  6. key=value / key: value pairs whose key names a secret (pin, password, totp, otp, token, secret, cookie,
 *     authorization, csrf, document, passport, phone, mobile, address, iban, card) → `key=[R]`.
 *  7. control characters removed; length capped.
 */
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const HEX_RE = /\b[0-9a-fA-F]{24,}\b/g;
const B64_RE = /(?<![A-Za-z0-9_-])[A-Za-z0-9_-]{32,}(?![A-Za-z0-9_-])/g;
const CODE_RE = /\b(?=[A-Z0-9]*\d)(?=[A-Z0-9]*[A-Z])[A-Z0-9]{8,}\b/g;
const D = "0-9\u0660-\u0669\u06F0-\u06F9";
const NUM_RE = new RegExp(`(?<![A-Za-z_${D}])[${D}](?:[${D},.\\u066B\\u066C \\-/]*[${D}])?`, "g");
// [C28 review R9] positions in stack frames (`file.ts:120:7`) are the only numbers kept after a colon
const POS_RE = /\.(?:[cm]?[jt]sx?)(?::\d+){1,2}/g;
// digits glued to letters (`x9876543`, account-like ids) when six or more
const GLUED_RE = /(?<=[A-Za-z_])\d{6,}/g;
// `Key (full_name)=(John Smith)` of PostgreSQL errors
const KEY_VALUE_RE = /=(\([^)]*\))/g;
// double-quoted values with a space or a non-ASCII letter (names, free text); identifiers stay
const QUOTED_RE = /"([^"]*)"/g;
const ARABIC_RE = /[\u0600-\u065F\u066E-\u06EF\u06FA-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+(?:[\s\u200C\u200F]+[\u0600-\u065F\u066E-\u06EF\u06FA-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+)*/g;
const SECRET_KEY = "(?:(?:full[_-]?)?name|customer(?:[_-]?name)?|beneficiary|pin|pass(?:word|code)?|totp|otp|token|secret|cookie|authori[sz]ation|csrf|document|passport|national[_-]?id|phone|mobile|address|iban|card(?:[_-]?code)?)";
const KV_RE = new RegExp(`\\b(${SECRET_KEY})(["']?\\s*[:=]\\s*)(\\[R\\]|"[^"]*"|'[^']*'|[^\\s,;&)}\\]]+)`, "gi");
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

/** Placeholder index in lower-case letters, so no rule below (digits, codes) can touch it. */
function letters(i: number): string {
  let s = "";
  let n = i;
  do {
    s = String.fromCharCode(97 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}
function fromLetters(s: string): number {
  let n = 0;
  for (const ch of s) n = n * 26 + (ch.charCodeAt(0) - 96);
  return n - 1;
}

function countDigits(s: string): number {
  let n = 0;
  for (const ch of s) if (/[0-9٠-٩۰-۹]/.test(ch)) n += 1;
  return n;
}

export function redactText(input: unknown, max = 1000): string {
  if (input === null || input === undefined) return "";
  // NFKC folds full-width and other compatibility digits into ASCII (review R9)
  let s = String(input).normalize("NFKC").replace(CONTROL_RE, "");
  const keep: string[] = [];
  const protect = (m: string) => {
    keep.push(m);
    return `\uE000${letters(keep.length - 1)}\uE000`;
  };
  s = s.replace(UUID_RE, (m) => protect(m.toLowerCase()));
  s = s.replace(POS_RE, protect);
  s = s.replace(KEY_VALUE_RE, "=([R])");
  s = s.replace(QUOTED_RE, (m, v: string) => (/\s|[^\x20-\x7E]/.test(v) && v !== "[R]" ? '"[Q]"' : m));
  s = s.replace(KV_RE, (_m, k: string, sep: string, v: string) => {
    const q = v.startsWith('"') ? '"' : v.startsWith("'") ? "'" : "";
    return `${k}${sep}${q}[R]${q}`;
  });
  s = s.replace(EMAIL_RE, "[E]");
  s = s.replace(HEX_RE, "[T]");
  s = s.replace(B64_RE, (m) => (/\d/.test(m) || /[_-]/.test(m) ? "[T]" : m));
  s = s.replace(CODE_RE, "[T]");
  s = s.replace(GLUED_RE, "[N]");
  s = s.replace(NUM_RE, (m) => (countDigits(m) >= 3 ? "[N]" : m));
  s = s.replace(ARABIC_RE, "[A]");
  s = s.replace(/\uE000([a-z]+)\uE000/g, (_m, i: string) => keep[fromLetters(i)] ?? "");
  if (s.length > max) s = s.slice(0, max - 1) + "…";
  return s;
}

/** A stack: each line redacted (positions `:line:col` survive), at most `maxFrames` lines and `max` characters. */
export function redactStack(input: unknown, maxFrames = 30, max = 8000): string | undefined {
  if (input === null || input === undefined || input === "") return undefined;
  const lines = String(input).split(/\r?\n/).slice(0, maxFrames + 1);
  // [C28 review R8] browser frames lose their origin (the office subdomain names the office); query strings and
  // fragments go too — only the origin-relative path and the position remain.
  const cleaned = lines.map((l) => redactText(l.replace(/https?:\/\/[^/\s)]+/g, "").replace(/(\/[^\s?#)]*)[?#][^\s:)]*/g, "$1"), 400));
  const out = cleaned.join("\n");
  return out.length > max ? out.slice(0, max - 1) + "…" : out;
}

/** An error's display name (`TypeError`, `ApiClientError`): letters, digits and `_ . -` only. */
export function safeErrorName(input: unknown): string {
  const s = typeof input === "string" ? input : "Error";
  const clean = s.replace(/[^A-Za-z0-9_.-]/g, "").slice(0, 100);
  return clean || "Error";
}

/** Path of a URL without query or fragment, with ids reduced to `:id` (used for client-reported pages). */
export function redactPath(input: unknown): string | undefined {
  if (typeof input !== "string" || !input) return undefined;
  let path = input;
  try {
    path = new URL(input, "http://x").pathname;
  } catch {
    path = input.split(/[?#]/)[0] ?? "";
  }
  // ids: UUIDs, numbers, and long mixed tokens (setup links, codes); short labels such as `v1` stay
  path = path.replace(UUID_RE, ":id").replace(/\/\d+(?=\/|$)/g, "/:n").replace(/\/(?=[^/]*\d)[^/]{8,}/g, "/:n");
  return redactText(path, 200);
}
